<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { t } from "@/i18n";
import { ModalDefaultInner } from "@easy/ui/modals";
import { CardIcon } from "@easy/ui/cards";
import { LayoutModal } from "@easy/ui/layouts";
import { InPageUsedTools } from "@/apps/page/page-project.interface";
import { useSections } from "@/apps/page/services/useSections";
import {
    TAB_NEW_PROJECT,
    useBindSectionWithProject,
} from "@/apps/page/project/sections/useBindWithProject";
import { PageSection } from "@/apps/page/page.interfaces";
import BindSectionWithToolProjectForm from "@/apps/page/project/sections/BindSectionWithToolProjectForm.vue";
import { useActions, useState } from "@/store/vuex-composition-mappers";

defineEmits(["close"]);
const props = defineProps<{ sectionId?: PageSection["id"]; tool: InPageUsedTools }>();

// @ts-expect-error
const { page } = useState("page", ["page"]);
// @ts-expect-error
const { connectToolSectionWithProject, bindToolSectionWithProject } = useActions("page", [
    "connectToolSectionWithProject",
    "bindToolSectionWithProject",
]);
const { sectionTypes } = useSections();

const iconComponent = computed(() => ({
    icon: sectionTypes.value.find((s) => s.tool === props.tool)?.icon,
    cssClass: `text-${props.tool}-500`,
}));

const showModal = ref(false);
const {
    selectedTab,
    chosenProjectId,
    shouldHaveExistingOption,
    toolProjects,
    project,
    projectNameError,
    bindWithProject,
    resetForm,
    onFieldBlur,
} = useBindSectionWithProject(props, {
    page,
    connectToolSectionWithProject,
    bindToolSectionWithProject,
});

const bindToolModalData = computed(() => ({
    title: t("page.bind_section_modal.title"),
    description: t(
        shouldHaveExistingOption.value
            ? "page.bind_section_modal.description"
            : "page.bind_section_modal.description_creation"
    ),
    ctaLabel: t(
        selectedTab.value === TAB_NEW_PROJECT
            ? "page.bind_section_modal.create_project"
            : "page.bind_section_modal.fill_with_project",
        { tool: props.tool }
    ),
}));

watch(
    props,
    (props) => {
        showModal.value = Boolean(props.tool);
    },
    { immediate: true }
);
</script>

<template>
    <LayoutModal v-if="showModal" v-model="showModal">
        <ModalDefaultInner
            v-model="showModal"
            :modal-data="bindToolModalData"
            @cta="bindWithProject"
            @close="$emit('close')"
        >
            <template #icon v-if="iconComponent.icon">
                <CardIcon>
                    <component
                        :is="iconComponent.icon"
                        class="h-4 w-4"
                        :class="iconComponent.cssClass"
                    />
                </CardIcon>
            </template>

            <div class="mt-3">
                <BindSectionWithToolProjectForm
                    :sectionId="sectionId"
                    :tool="tool"
                    :toolProjects="toolProjects"
                    :pageNameBlur="onFieldBlur"
                    :projectNameError="projectNameError"
                    @tabSwitched="resetForm"
                    @actionSubmit="bindWithProject"
                    v-model:selectedTab="selectedTab"
                    v-model:shouldHaveExistingOption="shouldHaveExistingOption"
                    v-model:chosenProjectId="chosenProjectId"
                    v-model:projectName="project.projectName"
                />
            </div>
        </ModalDefaultInner>
    </LayoutModal>
</template>
